import { Component, Inject } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import type { Image } from "../../media/media.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import {
  HARDCODED_VARIABLE_ICONS,
  hardcodedIconByKey,
  hardcodedIconByValue,
  hardcodedIconForVariable,
} from "../../digital-cards/variable-icons";
import type { HardcodedVariableIcon } from "../../digital-cards/digital-cards.interface";
import {
  BranchVariableFormDialogData,
  BranchVariableFormDialogResult,
  BranchVariableOption,
  BranchVariableSource,
} from "../branch-settings.interface";
import { mediaFilePreview } from "../../shared/utils/media-file-preview.util";

@Component({
  selector: "app-branch-variable-form-dialog",
  templateUrl: "./branch-variable-form-dialog.component.html",
  styleUrls: ["./branch-variable-form-dialog.component.scss"],
  standalone: false,
})
export class BranchVariableFormDialogComponent {
  public sendingRequest = false;
  public errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  public predefinedVariables: BranchVariableOption[];
  public mediaImages: Image[];
  public hardcodedIcons: HardcodedVariableIcon[] = HARDCODED_VARIABLE_ICONS;
  readonly mediaFilePreview = mediaFilePreview;

  public form: FormGroup<{
    source: FormControl<BranchVariableSource>;
    predefinedName: FormControl<string>;
    name: FormControl<string>;
    label: FormControl<string>;
    iconUrl: FormControl<string>;
  }>;

  private existingFieldNames: string[];

  constructor(
    public dialogRef: MatDialogRef<
      BranchVariableFormDialogComponent,
      BranchVariableFormDialogResult | undefined
    >,
    @Inject(MAT_DIALOG_DATA)
    public data: BranchVariableFormDialogData,
  ) {
    this.existingFieldNames =
      data.existingFieldNames?.map((name) => name.trim().toLowerCase()) ?? [];
    this.predefinedVariables = (data.predefinedVariables ?? []).filter(
      (variable) => !this.isExistingFieldName(variable.name),
    );
    this.mediaImages = data.mediaImages ?? [];
    const initialSource =
      this.predefinedVariables.length > 0 ? "predefined" : "custom";
    const initialIcon =
      initialSource === "predefined" && this.predefinedVariables[0]
        ? hardcodedIconForVariable(this.predefinedVariables[0].name).value
        : hardcodedIconByKey("customLink").value;

    this.form = new FormGroup({
      source: new FormControl(initialSource, { nonNullable: true }),
      predefinedName: new FormControl("", { nonNullable: true }),
      name: new FormControl("", { nonNullable: true }),
      label: new FormControl("", { nonNullable: true }),
      iconUrl: new FormControl(initialIcon, {
        nonNullable: true,
        validators: [(control) => Validators.required(control)],
      }),
    });
    this.applySourceValidators(initialSource);
  }

  get isPredefinedSource(): boolean {
    return this.form.controls.source.value === "predefined";
  }

  get isCustomSource(): boolean {
    return this.form.controls.source.value === "custom";
  }

  iconSrc(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    return hardcodedIconByValue(url)?.preview ?? url;
  }

  iconLabel(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    const hardcoded = hardcodedIconByValue(url);
    if (hardcoded) {
      return hardcoded.label;
    }
    return (
      this.mediaImages.find((image) => image.imageUrl === url)?.name?.trim() ||
      url
    );
  }

  submit = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.invalid) {
      return;
    }

    if (this.form.controls.source.value === "predefined") {
      const variable = this.findPredefinedVariable(
        this.form.controls.predefinedName.value,
      );
      if (!variable) {
        this.form.controls.predefinedName.setErrors({ required: true });
        return;
      }

      this.dialogRef.close({
        name: variable.name,
        label: this.getPredefinedLabel(variable),
        defaultValue: "",
        iconUrl: this.form.controls.iconUrl.value,
        overridable: variable.overridableByDefault ?? true,
        mandatory: false,
      });
      return;
    }

    this.dialogRef.close({
      name: this.form.controls.name.value.trim(),
      label: this.form.controls.label.value.trim(),
      defaultValue: "",
      iconUrl: this.form.controls.iconUrl.value,
      overridable: true,
      mandatory: false,
    });
  };

  close = () => {
    this.dialogRef.close(undefined);
  };

  sourceChanged = (source: BranchVariableSource): void => {
    this.applySourceValidators(source);
    this.form.patchValue({
      predefinedName: "",
      name: "",
      label: "",
      iconUrl: hardcodedIconByKey("customLink").value,
    });
  };

  predefinedVariableChanged = (name: string): void => {
    const variable = this.findPredefinedVariable(name);

    if (!variable) {
      this.form.patchValue({
        name: "",
        label: "",
        iconUrl: hardcodedIconByKey("customLink").value,
      });
      return;
    }

    this.form.patchValue({
      name: variable.name,
      label: this.getPredefinedLabel(variable),
      iconUrl: hardcodedIconForVariable(variable.name).value,
    });
  };

  private applySourceValidators(source: BranchVariableSource): void {
    const { predefinedName, name, label } = this.form.controls;

    if (source === "predefined") {
      predefinedName.setValidators((control) => Validators.required(control));
      name.clearValidators();
      label.clearValidators();
    } else {
      predefinedName.clearValidators();
      name.setValidators([
        (control) => Validators.required(control),
        (control) => Validators.pattern(/^\S*$/)(control),
        (control) => this.duplicateFieldValidator(control),
      ]);
      label.setValidators((control) => Validators.required(control));
    }

    predefinedName.updateValueAndValidity();
    name.updateValueAndValidity();
    label.updateValueAndValidity();
  }

  private duplicateFieldValidator(
    control: AbstractControl,
  ): ValidationErrors | null {
    const value = String(control.value ?? "")
      .trim()
      .toLowerCase();

    if (!value) return null;

    return this.isExistingFieldName(value) ? { duplicateField: true } : null;
  }

  private findPredefinedVariable(
    name: string,
  ): BranchVariableOption | undefined {
    return this.predefinedVariables.find((variable) => variable.name === name);
  }

  private getPredefinedLabel(variable: BranchVariableOption): string {
    return variable.label?.trim() || variable.name;
  }

  private isExistingFieldName(name: string): boolean {
    return this.existingFieldNames.includes(name.trim().toLowerCase());
  }
}
